import { EXCALIBUR_STATUS } from "../../excalibur/constants/constants";

// Rows per page on the Transaction Details task table. Matches the backend page size, so one
// backend call fills at most one page.
export const TASK_PAGE_SIZE = 100;

// How many pages beyond the one being viewed are kept fetched, so the pager can already show
// their numbers and clicking them is instant.
export const TASK_PAGES_AHEAD = 3;

// Safety net: no round makes more backend calls than this, even if the server keeps returning a
// new cursor. A round is at most one pass over the transaction at TASK_PAGE_SIZE rows per call, so
// this is the largest transaction a round reads to the end: 5000 x 100 = 500k rows, far above any
// real one. The cap is per round, not per loader, because the filing page takes three passes
// (FAILURE, ERROR, unfiltered) and one shared budget would cut the last pass short.
export const MAX_TASK_FETCHES_PER_ROUND = 5000;

// Task statuses that mean "this task failed", in the order they are shown. Tasks written by TES
// (generation, transformation, non-GIS filings) use FAILURE; tasks written by the GIS filing
// managers (France, Italy, Malaysia, Spain SII) carry the authority's word, ERROR.
export const FAILED_TASK_STATUSES = [EXCALIBUR_STATUS.FAILURE, EXCALIBUR_STATUS.ERROR];

/**
 * The failed statuses round 1 looks for. ERROR is only ever written by the authority's verdict on a
 * submitted document, so a transformation transaction can carry FAILURE but never ERROR; a filing
 * transaction can carry both at once. Each status costs one filtered pass over the transaction, so
 * the transformation page skips the ERROR pass.
 */
export function failedStatusesForRoute(isTransformation) {
    return isTransformation ? [EXCALIBUR_STATUS.FAILURE] : FAILED_TASK_STATUSES;
}

/** Rows that must be loaded for `pageIndex` to be shown with TASK_PAGES_AHEAD pages fetched after it. */
export function rowsNeededThrough(pageIndex, pageSize = TASK_PAGE_SIZE, pagesAhead = TASK_PAGES_AHEAD) {
    return (pageIndex + pagesAhead) * pageSize;
}

/** The slice of `rows` shown on 1-based `pageIndex`; empty when that page is not loaded. */
export function getTaskPage(rows, pageIndex, pageSize = TASK_PAGE_SIZE) {
    if (!Array.isArray(rows)) {
        return [];
    }
    const start = (pageIndex - 1) * pageSize;
    return rows.slice(start, start + pageSize);
}

/** Number of pages the loaded rows fill (at least 1, so the pager always has a page to stand on). */
export function getLoadedPageCount(rows, pageSize = TASK_PAGE_SIZE) {
    const length = Array.isArray(rows) ? rows.length : 0;
    return Math.max(1, Math.ceil(length / pageSize));
}

/**
 * Lazily loads a transaction's task rows failures-first, one backend page at a time.
 *
 * One filtered round per status in `failedStatuses` (status=FAILURE, then status=ERROR), each paged
 * until its cursor runs out; then one unfiltered round through the whole transaction, dropping the
 * rows it meets again by taskId. Rows are appended to one buffer in that order, so slicing the
 * buffer into pages gives: all failed tasks, then the rest, every page full except possibly the last.
 *
 * @param fetchPage (status, startKey) => Promise<{data, lastEvaluatedKey}>; status null = no filter
 * @returns {{loadUpTo: function(number): Promise<{rows: Array, done: boolean, capped: boolean, fetched: number}>, snapshot: function}}
 *          loadUpTo(n) fetches until at least n rows are buffered, the transaction is exhausted
 *          (done) or a round hits the fetch cap (capped, in which case done stays false), and resolves to
 *          a copy of the buffer, those two flags and `fetched`, the number of backend calls this
 *          call made (0 when the buffer already covered n). Calls are serialized, so overlapping
 *          loadUpTo calls never issue parallel requests for one loader.
 */
export function createTaskPageLoader(
    fetchPage,
    { maxFetchesPerRound = MAX_TASK_FETCHES_PER_ROUND, failedStatuses = FAILED_TASK_STATUSES } = {}
) {
    // The status each round sends; null is the final unfiltered round.
    const rounds = [...failedStatuses, null];
    const rows = [];
    const seenTaskIds = new Set();
    let round = 0;
    let startKey = null;
    let cursorsFollowed = new Set();
    let fetches = 0;
    // Backend calls made in the current round; reset when the round ends.
    let roundFetches = 0;
    // Set once a round hits the fetch cap: the loader stops for good, and `done` stays false because
    // the transaction was not read to the end.
    let capped = false;
    let queue = Promise.resolve();

    const isDone = () => round >= rounds.length;
    const snapshot = () => ({ rows: rows.slice(), done: isDone(), capped });

    const absorb = (data, filteredRound) => {
        if (!Array.isArray(data)) {
            return;
        }
        data.forEach(row => {
            // The unfiltered round reads the whole transaction again, so rows already buffered by an
            // earlier round come back; the taskId decides, not the status, so a task that failed
            // after its filtered round still shows up here instead of vanishing until Refresh.
            const taskId = row?.taskId;
            if (!taskId) {
                // taskId is the table's range key, so this should not happen. If it does, the row
                // cannot be recognised again later, so take it only once: from the unfiltered round.
                if (!filteredRound) {
                    rows.push(row);
                }
                return;
            }
            if (seenTaskIds.has(taskId)) {
                return;
            }
            seenTaskIds.add(taskId);
            rows.push(row);
        });
    };

    const endRound = () => {
        round += 1;
        startKey = null;
        cursorsFollowed = new Set();
        roundFetches = 0;
    };

    const fetchNext = async () => {
        const page = await fetchPage(rounds[round], startKey);
        fetches += 1;
        roundFetches += 1;
        absorb(page?.data, rounds[round] !== null);

        const nextKey = page?.lastEvaluatedKey;
        if (!nextKey) {
            endRound();
            return;
        }
        // A cursor seen before in this round means the server is looping; treat it as the round's end.
        const cursorId = typeof nextKey === "string" ? nextKey : JSON.stringify(nextKey);
        if (cursorsFollowed.has(cursorId)) {
            endRound();
            return;
        }
        cursorsFollowed.add(cursorId);
        startKey = nextKey;
    };

    const loadUpTo = targetRowCount => {
        const run = queue.then(async () => {
            const fetchesBefore = fetches;
            while (!isDone() && !capped && rows.length < targetRowCount) {
                if (roundFetches >= maxFetchesPerRound) {
                    // Stop fetching, but do not pretend the transaction was read to the end.
                    capped = true;
                    break;
                }
                // eslint-disable-next-line no-await-in-loop
                await fetchNext();
            }
            return { ...snapshot(), fetched: fetches - fetchesBefore };
        });
        // A failed load must not poison the queue: the next call retries from the same cursor.
        queue = run.catch(() => undefined);
        return run;
    };

    return { loadUpTo, snapshot };
}
